// Npm
import { Button, Card, Icon, Pagination } from '@nt/dds-react';
import { MasterDetailModule } from 'ag-grid-enterprise';
import moment from 'moment';
import React, { FC, memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import DataGrid from '@northerntrust-internal/nds-datagrid';
import '@northerntrust-internal/nds-datagrid/lib/datagrid.css';

// Local
import { useApAsyncDispatch, useAppSelector } from '../../../app/hooks';
import { fetchReferenceDataInformation } from '../../../pages/Create/CreateSlice';
import { selectUserDetails } from '../../../pages/Home/HomeSlice';
import { axiosApiCall } from '../../../services/apiService';
import { objectToQueryString } from '../../../services/common';
import { getDataGridLicenseKey, defaultColDef } from '../../../utils/helper';
import {
  ApiResponse,
  IPagination,
  ItemsInProgressAccountData,
  ItemsInProgressDraftData,
  ItemsInprogressGridData,
  StatusType
} from '../../../utils/types';
import { inProgressColumnDefs, inProgressDetailCellRendererParams } from '../config';
import LinkButtonCellRenderer from './LinkButtonCellRenderer';
import StatusButtonCellRenderer from './StatusButtonCellRenderer';
import './ItemsInProgress.css';

interface Props {}

/**
 * @param {*} {}
 * @return {ReactElement}
 */
const ItemsInProgress: FC<Props> = (): ReactElement => {
  const storeAsyncDispatch = useApAsyncDispatch();
  const userDetails = useAppSelector(selectUserDetails);
  const paginationData = { totalRecords: 0, pageNo: 0, pageSize: 10 };

  const [draftData, setDraftData] = useState<ItemsInProgressDraftData[]>([]);
  const [paginationDetails, setPaginationDetails] = useState<IPagination>(paginationData);
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPage, setTotalPage] = useState<number>();
  const [api, setApi] = useState<any>();
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const gridModules = useMemo(() => [MasterDetailModule], []);
  const licenseKey = useMemo(() => getDataGridLicenseKey(), []);

  const components = useMemo(
    () => ({
      detailCellRenderer: LinkButtonCellRenderer,
      statusCellRenderer: StatusButtonCellRenderer
    }),
    []
  );

  const inProgressGridColumnDefs = useMemo(() => inProgressColumnDefs.map(columnDef => ({ ...columnDef })), []);

  const detailCellRendererParams = useMemo(
    () => ({
      ...inProgressDetailCellRendererParams,
      detailGridOptions: {
        ...inProgressDetailCellRendererParams.detailGridOptions,
        components,
        columnDefs: inProgressDetailCellRendererParams.detailGridOptions.columnDefs.map(columnDef => ({ ...columnDef })),
        getRowId: (params: any) => `${params?.data?.draftId}-${params?.data?.accountName}`
      }
    }),
    [components]
  );

  useEffect(() => {
    setCurrentPage(paginationDetails?.pageNo);
    setTotalPage(Math.ceil((paginationDetails?.totalRecords as number) / paginationDetails?.pageSize));
  }, [paginationDetails]);

  const getItemsInProgressData = async (page: number, pageSize: number, sponsorCode: string) => {
    const queryParams = { pageNo: page, pageSize: pageSize, sponsorCode: sponsorCode };
    const queryString = objectToQueryString(queryParams);
    const rulesUrl: string = process.env.UXP_STATUS_TRACKING as string;
    const responseData: ApiResponse = await axiosApiCall('GET', `${rulesUrl}/status?${queryString}`);
    if (responseData?.meta?.records > 0) {
      setPaginationDetails(responseData?.pagination as IPagination);
      setDraftData(responseData.data.resultSet as ItemsInProgressDraftData[]);
    }
  };

  useEffect(() => {
    if (typeof userDetails?.personaId === 'number' && draftData.length === 0) {
      getItemsInProgressData(0, 10, userDetails?.sponsorCode as string);
      storeAsyncDispatch(fetchReferenceDataInformation());
    }
  }, [userDetails?.personaId]);

  const onFirstDataRendered = useCallback((params: any) => {
    setApi(params.api);
  }, []);

  const getDetailCellRendererParams = useCallback(
    (params: any) => {
      if (params.data.children.length !== 0) return detailCellRendererParams;
    },
    [detailCellRendererParams]
  );

  const getRowId = useCallback((params: any) => String(params?.data?.draftId), []);

  const isRowMaster = useCallback((dataItem: ItemsInprogressGridData) => Boolean(dataItem?.children?.length), []);

  const changePage = (page: number) => {
    getItemsInProgressData(page, 10, userDetails?.sponsorCode as string);
    api?.paginationGoToPage(page);
    setCurrentPage(page);
  };
  const getFormattedDate = (date: string): string => {
    return date ? moment(date).format('DD-MMM-YYYY') : '';
  };

  const getAccountRequestedDate = (item: ItemsInProgressDraftData): string => {
    let returnValue: string = '';
    if (item?.accountList && item?.accountList.length > 0) {
      returnValue = item.accountList[0].accountRequestedDate;
    }
    return returnValue;
  };

  const inProgressGridData: ItemsInprogressGridData[] = useMemo(
    () =>
      draftData.map((item: ItemsInProgressDraftData) => ({
        draftId: item.draftId,
        legalEntityName: item.legalEntityName,
        submittedDate: getFormattedDate(item?.legalEntitySubmittedDate),
        statusSubmittedDate: item?.legalEntitySubmittedDate,
        type: item?.type,
        status: item.status,
        total: `Total: ${item?.accountList?.length}`,
        draftCreateDate: getFormattedDate(item.draftCreateDate),
        statusDraftCreateDate: item.draftCreateDate,
        statusAccountRequestedDate: getAccountRequestedDate(item),
        viewDetails: (item.status as StatusType) === StatusType.IN_PROGRESS && item?.accountList?.length === 0,
        children: item?.accountList?.map((account: ItemsInProgressAccountData) => ({
          draftId: item.draftId,
          accountName: account.accountName,
          submittedDate: getFormattedDate(account.accountSubmittedDate),
          statusSubmittedDate: account.accountSubmittedDate,
          accountType: account.type,
          status: account.status,
          draftCreateDate: getFormattedDate(item.draftCreateDate),
          statusDraftCreateDate: item.draftCreateDate,
          legalEntityName: item.legalEntityName,
          statusAccountRequestedDate: account.accountRequestedDate,
          viewDetails: (item.status as StatusType) === StatusType.IN_PROGRESS
        }))
      })),
    [draftData]
  );

  return (
    <Card hover={false} noPadding={true} shadowSize="xs" className="faq-wrap">
      <div className="home-card-header-wrap">
        <h2 className="roboto-font card-heading">Items in Progress</h2>
        {!showPagination && (
          <Button
            danger={undefined}
            icon="none"
            iconName="settings"
            kind="borderless"
            data-testid="view-all-button"
            onClick={() => setShowPagination(true)}
            size="small"
            type="button"
          >
            view all
          </Button>
        )}
      </div>
      <div className="card-accordion-inprogress-wrap">
        {inProgressGridData.length !== 0 && (
          <>
            <div className={`${showPagination ? 'paddingbottom-xl' : ''}`}>
              <DataGrid
                suppressContextMenu
                modules={gridModules}
                rowData={
                  inProgressGridData.length > 3 && !showPagination ? inProgressGridData.slice(0, 3) : inProgressGridData
                }
                columnDefs={inProgressGridColumnDefs}
                detailCellRendererParams={getDetailCellRendererParams}
                components={components}
                defaultColDef={defaultColDef}
                groupDefaultExpanded={0}
                detailRowAutoHeight={true}
                masterDetail={true}
                domLayout="autoHeight"
                pagination={true}
                suppressPaginationPanel={true}
                paginationAutoPageSize={false}
                paginationPageSize={paginationDetails?.pageSize}
                onFirstDataRendered={onFirstDataRendered}
                getRowId={getRowId}
                isRowMaster={isRowMaster}
                licenseKey={licenseKey}
                gridHeight={'auto'}
              />
            </div>
            {showPagination && (
              <Pagination
                id="pagination-id"
                page={currentPage}
                pages={totalPage}
                pageSize={paginationData.pageSize}
                onPageChange={(page: any) => changePage(page)}
                showPageSizeOptions={false}
                nextVersion
              />
            )}
          </>
        )}
        {inProgressGridData.length === 0 && (
          <div className="no-item-wrap">
            <Icon ariaHidden name="feedback" className="no-item-icon" type="info" />
            <h3 className="roboto-font no-item-description">No Items in Progress</h3>
          </div>
        )}
      </div>
    </Card>
  );
};

ItemsInProgress.displayName = 'ItemsInProgress';
export default memo(ItemsInProgress);
